import { getAuth } from "firebase/auth";
import Vue from "vue";
import VueRouter, { type RouteConfig } from "vue-router";
// Dashboard layout
import DashboardLayout from "@/layouts/DashboardLayout.vue";

// Top layout
import TopLayout from "@/layouts/TopLayout.vue";
import store from "@/store";
import CampaignResults from "@/views/Dashboard/CampaignResults.vue";
import ConversationAnalysis from "@/views/Dashboard/ConversationAnalysis.vue";
import EditPackage from "@/views/Dashboard/EditPackage.vue";
import Home from "@/views/Dashboard/HomeView.vue";
import Learners from "@/views/Dashboard/LearnersView.vue";
import NewLearners from "@/views/Dashboard/NewLearnersView.vue";
import NewPackage from "@/views/Dashboard/NewPackage.vue";
import PackageResults from "@/views/Dashboard/PackageResults.vue";
import Settings from "@/views/Dashboard/SettingsView.vue";
import SignOut from "@/views/Dashboard/SignOutView.vue";
import CheckEmailView from "@/views/Top/CheckEmailView.vue";
import ResetPasswordView from "@/views/Top/ResetPasswordView.vue";
import SignIn from "@/views/Top/SignInView.vue";
import SignUp from "@/views/Top/SignUpView.vue";
import Verified from "@/views/Top/VerifiedView.vue";

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: "/",
    name: "InteLLA",
    redirect: "signin",
    component: TopLayout,
    children: [
      {
        path: "signin",
        name: "Sign In",
        component: SignIn,
        props: (route) => ({
          snackbarAction: Boolean(route.params.snackbarAction),
          snackbarText: String(route.params.snackbarText),
          snackbarColor: String(route.params.snackbarColor),
        }),
      },
      {
        path: "signup",
        name: "Sign Up",
        component: SignUp,
      },
      {
        path: "verified",
        name: "Verified",
        component: Verified,
      },
      {
        path: "reset-password",
        name: "Reset Password",
        component: ResetPasswordView,
      },
      {
        path: "check-email",
        name: "Check Email",
        component: CheckEmailView,
      },
    ],
  },
  {
    path: "/dashboard",
    name: "InteLLA Customer Console",
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: "home",
        name: "Home",
        component: Home,
      },
      {
        path: "settings",
        name: "Settings",
        component: Settings,
      },
      {
        path: "exam/new",
        redirect: { name: "Home" },
      },
      {
        path: "learners",
        name: "Learners",
        component: Learners,
      },
      {
        path: "newlearners",
        name: "New Learners",
        component: NewLearners,
      },
      {
        path: "signout",
        name: "Sign Out",
        component: SignOut,
      },
      {
        path: "campaign-results/:campaignCode",
        name: "Campaign Results",
        component: CampaignResults,
      },
      {
        path: "package-results/:packageCode",
        name: "Package Results",
        component: PackageResults,
      },
      {
        path: "conversation-analysis/:dialogId",
        name: "Conversation Analysis",
        component: ConversationAnalysis,
      },
      {
        path: "package/new",
        name: "New Package",
        component: NewPackage,
      },
      {
        path: "package/edit/:packageId",
        name: "Edit Package",
        component: EditPackage,
      },
    ],
  },
  {
    path: "/*",
    redirect: "signin",
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    // Use onAuthStateChanged to wait for Firebase auth to initialize
    // This is critical for page refreshes
    getAuth().onAuthStateChanged((user) => {
      if (!store.state.isCreatingUser) {
        if (user && store.state.email) {
          // Additional check: ensure the stored UID matches the current Firebase user
          const storedUID = store.getters.uid;
          if (storedUID && storedUID !== user.uid) {
            // Different user detected, redirect to sign in
            next({ name: "Sign In", query: { redirect: to.path } });
            return;
          }

          // Check authStatus to ensure isAccepted check has completed
          const authStatus = store.getters.authStatus;
          if (authStatus === "authenticated") {
            // User is fully authenticated and accepted
            next();
          } else if (authStatus === "pending") {
            // Still checking auth status, wait for it to complete
            const unwatch = store.watch(
              (state) => state.authStatus,
              (newStatus) => {
                unwatch();
                if (newStatus === "authenticated") {
                  next();
                } else {
                  next({ name: "Sign In", query: { redirect: to.path } });
                }
              },
            );
          } else {
            // Auth check failed (user not accepted)
            next({ name: "Sign In", query: { redirect: to.path } });
          }
        } else {
          next({ name: "Sign In", query: { redirect: to.path } });
        }
      }
    });
  } else if (to.name === "Sign In") {
    // Prevent authenticated users from accessing the sign-in page
    getAuth().onAuthStateChanged((user) => {
      if (user && store.state.email && store.getters.uid === user.uid) {
        // User is already authenticated, redirect to sign out page
        next({ name: "Sign Out" });
      } else {
        // User is not authenticated or data doesn't match, allow access to sign-in page
        next();
      }
    });
  } else {
    next();
  }
});

export default router;
